import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

export function BottomSheet({
  open,
  onClose,
  title,
  subtitle,
  leading,
  fullHeight,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  leading?: ReactNode;
  fullHeight?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}) {
  // Portal to body so the sheet's `position: fixed` is always anchored to
  // the viewport. Without this, any transformed/backdrop-filtered
  // ancestor (e.g. PullToRefresh's translateY wrapper, Header's
  // backdrop-blur) creates a containing block for fixed descendants and
  // the sheet docks to that ancestor's box instead of the screen bottom.
  const sheet = (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="bs-backdrop"
            className="fixed inset-0 z-50 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="bs-panel"
            className={`fixed left-0 right-0 bottom-0 z-50 bg-background rounded-t-2xl shadow-xl flex flex-col ${fullHeight ? 'h-[96vh]' : 'max-h-[85vh]'}`}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="flex items-center justify-between px-4 h-12 border-b border-border">
              <div className="flex items-center gap-2">
                {leading}
                <div className="flex flex-col leading-tight">
                  <span className="font-medium text-sm">{title}</span>
                  {subtitle && (
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{subtitle}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded hover:bg-accent"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">{children}</div>
            {footer && (
              <div className="shrink-0 border-t border-border px-4 py-3">{footer}</div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(sheet, document.body);
}
